import { IBookingsSlotsData, IFrontDeskDoctorRotaResponseData } from "@/api_framework/api_modals/Bookings";
import Input from "@/app/components/ui/Inputs/Input";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import {
  setOverbookingFlag,
  setSelectedSlotDetails,
  setSelectedSlotStaffDetails,
  setSelectedSlotsClinicDetails,
} from "@/app_redux/reducers/slices/auth/slots_slice";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import CommentSectionModal from "../CommentSection";
import { FRONT_DESK_OB_LOG_SOURCE, getStartTimeOptions } from "./helpers";

interface IFrontDeskOverbookingRowFormFields {
  hours: string;
  minutes: string;
  end_hours: string;
  end_minutes: string;
}

interface IFrontDeskOverbookingRowProps {
  rota: IFrontDeskDoctorRotaResponseData;
  clinicId?: string;
  duration: number; // minutes
  date: string;
  service: { id: string; title: string };
  subService: { id: string; title: string };
  hideHeader: boolean;
}

const HEADERS = [
  { title: "Provider", className: "w-28" },
  { title: "Clinic", className: "w-24" },
  { title: "Date", className: "w-24" },
  { title: "Service L1", className: "w-36" },
  { title: "Service L2", className: "w-36" },
  { title: "Start Time", className: "w-48 pl-2" },
  { title: "End Time", className: "w-48 " },
];

/* Same UI as OverBookingTableForm; service / sub service come from the filters, provider from doctor ROTA */
const FrontDeskOverbookingRow: React.FC<IFrontDeskOverbookingRowProps> = ({
  rota,
  clinicId,
  duration,
  date,
  service,
  subService,
  hideHeader,
}) => {
  const dispatch = useAppDispatch();
  const { addMinutesAndCalculateTime } = useUtils();
  const { getRouteKey } = useRouter();
  const { customerId, petId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);
  const { execute: logClick } = useClickLogAPI();
  const formHook = useForm<IFrontDeskOverbookingRowFormFields>({ mode: "onChange", defaultValues: {} });

  /* Constants Starts */
  const startTimeOptions = getStartTimeOptions(date, duration);
  const hoursWatch = formHook.watch("hours");
  const minutesWatch = formHook.watch("minutes");
  const hoursItems = Object.keys(startTimeOptions)
    .sort()
    .map((h) => ({ id: h, title: h }));
  const minuteItems = (startTimeOptions[hoursWatch] ?? []).map((m) => ({ id: m, title: m }));
  const serviceItems = service?.id ? [service] : [];
  const subServiceItems = subService?.id ? [subService] : [];
  /* Constants Ends */

  /* Actions / Handlers Starts */
  const getEndTime = (hours: string, minutes: string) =>
    hours && minutes ? addMinutesAndCalculateTime(`${hours}:${minutes}`, duration).newTime : "";

  const setStartTime = (hours: string, minutes: string) => {
    formHook.setValue("hours", hours);
    formHook.setValue("minutes", minutes);
    const [endHours = "", endMinutes = ""] = getEndTime(hours, minutes).split(":");
    formHook.setValue("end_hours", endHours);
    formHook.setValue("end_minutes", endMinutes);
  };

  const handleSubmit = async (): Promise<boolean> => {
    const isValid = await formHook.trigger();
    if (!isValid) return false;

    if (!clinicId) {
      toast.error("Clinic not found for this provider, please select the clinic in filters.");
      return false;
    }
    const { hours, minutes } = formHook.getValues();
    // Time moves on while the page is open, so the picked start time may have passed
    if (!getStartTimeOptions(date, duration)[hours]?.includes(minutes)) {
      toast.error("Selected start time has passed, please select again.");
      setStartTime(hours, "");
      return false;
    }

    const startTime = `${hours}:${minutes}:00`;
    const endTime = `${getEndTime(hours, minutes)}:00`;
    logClick({
      event_type: "FRONT_DESK_OB_BOOK_NOW_CLICK",
      source: FRONT_DESK_OB_LOG_SOURCE,
      scr_id: "BOOKING_PAGE",
      client_id: customerId,
      pet_id: petId,
      vms_user_id: vmsUserId,
      clinic_id: clinicId,
      clinic_name: rota?.clinic_name,
      staff_id: rota?.staff_id ?? null,
      cta_text: "Book Now",
      filter_values: {
        booking_date: date,
        start_time: startTime,
        end_time: endTime,
        provider_name: rota?.provider_name ?? null,
        service_type_id: service?.id ?? null,
        sub_service_id: subService?.id ?? null,
        slot_duration: duration,
      },
      timestamp: Date.now(),
    });

    dispatch(setOverbookingFlag(true));
    dispatch(
      setSelectedSlotDetails({
        data: {
          slot_id: "",
          start_time: startTime,
          end_time: endTime,
        } as IBookingsSlotsData,
      }),
    );
    dispatch(
      setSelectedSlotStaffDetails({
        data: {
          experience: "",
          expertise: "",
          staff_id: rota?.staff_id ?? null,
          staff_name: rota?.provider_name ?? "",
        },
      }),
    );
    dispatch(
      setSelectedSlotsClinicDetails({
        data: { clinic_id: clinicId, clinic_name: rota?.clinic_name ?? "", vetbuddy_clinic_name: "" },
      }),
    );
    return true;
  };
  /* Actions / Handlers Ends */

  return (
    <form onSubmit={(event) => event.preventDefault()}>
      <table className="w-full">
        <thead className="border-b-2 pb-2">
          <tr>
            {HEADERS.map((header) => (
              <th
                key={header.title}
                className={clsx(
                  "px-3 py-3 text-sm text-left font-medium",
                  header.className,
                  hideHeader ? " p-0 invisible" : "px-3 py-3",
                )}
              >
                {header.title}
              </th>
            ))}
            <th
              className={clsx("px-3 py-3 text-sm text-center font-medium", hideHeader ? " p-0 invisible" : "px-3 py-3")}
            >
              Book
            </th>
          </tr>
        </thead>
        <tbody className="pt-2">
          <tr className="pt-2">
            <td className="text-gray-600 text-xs py-2 px-3">{rota?.provider_name || "-"}</td>
            <td className="text-gray-600  text-xs py-2 px-3">{rota?.clinic_name || "-"}</td>
            <td className="text-gray-600 text-xs py-2 px-3">{rota?.date || date || "-"}</td>
            <td>
              <div>
                <SearchableSelectMenu
                  disabled
                  showDropdownIcon
                  showTypedErrors={false}
                  errorMessages={[]}
                  fieldError={null}
                  register={null}
                  placeholder="Select Service"
                  selectItems={serviceItems}
                  defaultSelected={serviceItems[0]}
                  autoComplete="false"
                  containerClassName="w-full"
                />
              </div>
            </td>
            <td>
              <div>
                <SearchableSelectMenu
                  disabled
                  showDropdownIcon
                  showTypedErrors={false}
                  errorMessages={[]}
                  fieldError={null}
                  register={null}
                  listBoxClassName="w-72"
                  placeholder="Select Sub Service"
                  containerClassName="ml-2 w-full"
                  selectItems={subServiceItems}
                  defaultSelected={subServiceItems[0]}
                  autoComplete="false"
                />
              </div>
            </td>
            <td>
              <div className="grid grid-cols-2 gap-1 pl-4">
                <div>
                  <NonSearchableSelectMenu
                    disabled={hoursItems.length === 0}
                    onSelectItem={(item) => setStartTime(item?.id ?? "", "")}
                    showTypedErrors={false}
                    errorMessages={[{ message: "Required", type: "required" }]}
                    fieldError={formHook.formState.errors.hours}
                    register={formHook.register("hours", { required: true })}
                    selectItems={hoursItems}
                    defaultSelected={hoursItems.find((h) => h.id === hoursWatch)}
                    showDropdownIcon
                    placeholder="Hours"
                  />
                </div>
                <div>
                  <NonSearchableSelectMenu
                    disabled={!hoursWatch}
                    onSelectItem={(item) => setStartTime(hoursWatch, item?.id ?? "")}
                    showTypedErrors={false}
                    errorMessages={[{ message: "Required", type: "required" }]}
                    fieldError={formHook.formState.errors.minutes}
                    register={formHook.register("minutes", { required: true })}
                    selectItems={minuteItems}
                    defaultSelected={minuteItems.find((m) => m.id === minutesWatch)}
                    showDropdownIcon
                    placeholder="Minutes"
                  />
                </div>
              </div>
            </td>
            <td>
              <div className="grid grid-cols-2 gap-1 pl-2">
                <div>
                  <Input
                    disabled
                    className="disabled:bg-gray-100 text-xs"
                    placeholder="Hour"
                    register={formHook.register("end_hours")}
                    errorMessages={[]}
                    fieldError={formHook.formState.errors.end_hours}
                  />
                </div>
                <div>
                  <Input
                    disabled
                    className="disabled:bg-gray-100 text-xs"
                    placeholder="Minutes"
                    register={formHook.register("end_minutes")}
                    errorMessages={[]}
                    fieldError={formHook.formState.errors.end_minutes}
                  />
                </div>
              </div>
            </td>
            <td className="pl-4 ">
              <div className="w-full h-full flex justify-center items-center flex-col">
                <CommentSectionModal
                  onClick={handleSubmit}
                  buttonName="Book Now"
                  activeButton={hoursItems.length > 0}
                  overBooking
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </form>
  );
};

export default FrontDeskOverbookingRow;
